import { useEffect, useState } from "react";
import { Engraved, Icon, Script } from "../components/ui";
import type { IconName, ScreenId } from "../components/ui";
import { FloralSpray, Ornament } from "../components/Florals";
import type { FloralDensity } from "../components/Florals";
import { PhotoTile } from "../components/PhotoTile";
import type { LightboxTile } from "../components/Lightbox";
import { useBranding, useKeep, useKeepTheme, useKeepType, useSettings } from "../lib/keep";
import { keepTypeDef } from "@wedding/shared";
import { api } from "../lib/api";

export function useCountUp(target: number, ms = 1100): number {
  const [n, setN] = useState(0);
  useEffect(() => {
    let raf = 0;
    let start: number | undefined;
    const tick = (t: number) => {
      if (!start) start = t;
      const p = Math.min(1, (t - start) / ms);
      const e = 1 - Math.pow(1 - p, 3);
      setN(Math.round(target * e));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, ms]);
  return n;
}

interface HomeScreenProps {
  go: (s: ScreenId) => void;
  totalPhotos: number;
  density: FloralDensity;
  recent: number[];
  openLightbox: (tiles: LightboxTile[], index: number) => void;
}

interface RecentTile {
  key: string;
  seed?: number;
  src?: string;
  video?: boolean;
}

export function HomeScreen({ go, totalPhotos, density, recent, openLightbox }: HomeScreenProps) {
  const { keepId, live } = useKeep();
  const branding = useBranding();
  const kicker = keepTypeDef(useKeepType()).kicker;
  const faceMatch = useSettings().faceMatch;
  const canUpload = useSettings().acceptingUploads !== false;
  const scriptClass = useKeepTheme().scriptClass;
  // Split "A & B" so the ampersand keeps its stylized treatment; render as-is otherwise.
  const [nameA, nameB] = branding.displayName.split(/\s*&\s*/);
  const [total, setTotal] = useState(totalPhotos);
  const [liveTiles, setLiveTiles] = useState<RecentTile[] | null>(null);
  useEffect(() => {
    if (!live || !keepId) return;
    api
      .galleryCounts(keepId)
      .then((r) => setTotal(r.totalPhotos))
      .catch((err) => console.warn("counts fetch failed:", err));
    api
      .recentPhotos(keepId)
      .then((r) => setLiveTiles(r.photos.map((p) => ({ key: p.photoId, src: p.thumbUrl, video: p.kind === "video" }))))
      .catch((err) => console.warn("recent fetch failed:", err));
  }, [live, keepId]);
  const count = useCountUp(total);

  // Mock placeholder tiles for the demo; real thumbnails once live. Hide the strip when a
  // live event has no photos yet (don't show fake tiles).
  const recentTiles: RecentTile[] = live ? (liveTiles ?? []) : recent.map((s) => ({ key: String(s), seed: s }));
  const showRecent = recentTiles.length > 0;
  const recentLb: LightboxTile[] = recentTiles.map((t) => ({ seed: t.seed, src: t.src, video: t.video }));
  const Card = ({
    icon,
    kicker,
    title,
    desc,
    onClick,
    tone,
  }: {
    icon: IconName;
    kicker: string;
    title: string;
    desc: string;
    onClick: () => void;
    tone: "rose" | "blue" | "plum";
  }) => (
    <button className={"action-card tone-" + tone} onClick={onClick}>
      <span className="action-ic">
        <Icon name={icon} size={24} stroke={1.6} />
      </span>
      <span className="action-text">
        <Engraved size={11} className="action-kicker">
          {kicker}
        </Engraved>
        <span className="action-title">{title}</span>
        <span className="action-desc">{desc}</span>
      </span>
      <Icon name="chevron" size={18} className="action-chev" />
    </button>
  );
  return (
    <div className="screen home">
      <div className="home-hero">
        <FloralSpray variant="topBorder" density={density} width={400} height={150} seed={11} className="hero-garland" />
        <div className="hero-inner">
          {branding.copy.logoUrl && (
            <img
              src={branding.copy.logoUrl}
              alt=""
              style={{ maxWidth: 120, maxHeight: 80, objectFit: "contain", margin: "0 auto 14px", display: "block" }}
            />
          )}
          <Engraved size={11.5} style={{ letterSpacing: "0.42em", marginLeft: "0.42em" }}>
            {kicker}
          </Engraved>
          <Script size={62} className={"hero-names " + scriptClass}>
            {nameB ? (
              <>
                {nameA}
                <span className="amp"> &amp; </span>
                {nameB}
              </>
            ) : (
              branding.displayName
            )}
          </Script>
          <div className="hero-orn">
            <Ornament color="var(--c-ink-soft)" width={132} />
          </div>
          {branding.eventDate && (
            <Engraved size={12} style={{ letterSpacing: "0.34em", marginLeft: "0.34em" }}>
              {branding.eventDate}
            </Engraved>
          )}
          <p className="hero-tag">
            {branding.tagline || "A living album of our day, written by everyone who shared it."}
          </p>
        </div>
      </div>

      <div className="home-count">
        <span className="count-num">{count.toLocaleString()}</span>
        <Engraved size={11} className="count-lab">
          Memories shared · and counting
        </Engraved>
      </div>

      <div className="actions">
        {canUpload && (
          <Card
            icon="upload"
            kicker="In a few taps"
            title="Add your photos"
            tone="rose"
            desc="Share what you captured — no app, no account."
            onClick={() => go("upload")}
          />
        )}
        {faceMatch && (
          <Card
            icon="face"
            kicker="Powered by your selfie"
            title="Find photos of you"
            tone="blue"
            desc="One selfie finds every photo you're in."
            onClick={() => go("findme")}
          />
        )}
        <Card
          icon="sparkle"
          kicker="Organized for you"
          title="Explore the moments"
          tone="plum"
          desc="From the vows to the very last dance."
          onClick={() => go("moments")}
        />
      </div>

      {showRecent && (
        <div className="home-recent">
          <div className="row-head">
            <Engraved size={12}>Just Added</Engraved>
            <button className="link-mini" onClick={() => go("all")}>
              See all
            </button>
          </div>
          <div className="recent-strip">
            {recentTiles.map((t, i) => (
              <PhotoTile
                key={t.key}
                seed={t.seed}
                src={t.src}
                video={t.video}
                ratio="3 / 4"
                radius={6}
                className="recent-tile"
                onClick={() => openLightbox(recentLb, i)}
              />
            ))}
          </div>
        </div>
      )}

      <div className="home-foot">
        <Script size={34} style={{ color: "var(--c-accent)" }}>
          Here's to forever
        </Script>
        {branding.eventDateNumeric && (
          <Engraved size={10.5} style={{ letterSpacing: "0.3em", color: "var(--c-ink-soft)" }}>
            {branding.eventDateNumeric}
          </Engraved>
        )}
      </div>
    </div>
  );
}
